/**
 * Enhanced template function that supports loops, conditionals, and variable replacement
 * @param {string} template - Template string with placeholders
 * @param {object} data - Data object containing values to replace
 * @returns {string} - Rendered template string
 */
export function renderTemplate(template, data) {

    let result = template;

    // Handle for loops with key-value pairs: {% for key, item in views %}
    result = result.replace(/\{%\s*for\s+(\w+),\s*(\w+)\s+in\s+([^%]+)\s*%\}(.*?)\{%\s*endfor\s*%\}/gs, (match, keyVar, itemVar, objectPath, loopContent) => {
        const obj = getNestedValue(data, objectPath.trim());

        if (obj && typeof obj === 'object') {
            const entries = Object.entries(obj);
            return entries.map(([key, item], index) => {
                // Create context for each loop iteration
                const loopContext = {
                    ...data,
                    [keyVar]: key,
                    [itemVar]: item,
                    loop: {
                        index: index,
                        index0: index,
                        index1: index + 1,
                        first: index === 0,
                        last: index === entries.length - 1,
                        length: entries.length,
                        revindex: entries.length - index,
                        revindex0: entries.length - index - 1
                    }
                };
                return renderTemplate(loopContent, loopContext);
            }).join('');
        }
        return '';
    });

    // Handle for loops with single variable: {% for item in items %}
    result = result.replace(/\{%\s*for\s+(\w+)\s+in\s+([^%]+)\s*%\}(.*?)\{%\s*endfor\s*%\}/gs, (match, itemVar, arrayPath, loopContent) => {
        const array = getNestedValue(data, arrayPath.trim());

        if (Array.isArray(array)) {
            return array.map((item, index) => {
                // Create context for each loop iteration
                const loopContext = {
                    ...data,
                    [itemVar]: item,
                    loop: {
                        index: index,
                        index0: index,
                        index1: index + 1,
                        first: index === 0,
                        last: index === array.length - 1,
                        length: array.length,
                        revindex: array.length - index,
                        revindex0: array.length - index - 1
                    }
                };
                return renderTemplate(loopContent, loopContext);
            }).join('');
        }

        // If it's an object, treat it like key-value pairs
        if (array && typeof array === 'object') {
            const entries = Object.entries(array);
            return entries.map(([key, item], index) => {
                // Create context for each loop iteration
                const loopContext = {
                    ...data,
                    [itemVar]: { key: key, value: item }, // Wrap in object for consistency
                    loop: {
                        index: index,
                        index0: index,
                        index1: index + 1,
                        first: index === 0,
                        last: index === entries.length - 1,
                        length: entries.length,
                        revindex: entries.length - index,
                        revindex0: entries.length - index - 1
                    }
                };
                return renderTemplate(loopContent, loopContext);
            }).join('');
        }

        return '';
    });

    // Handle simple conditionals: {% if condition %}content{% endif %}
    result = result.replace(/\{%\s*if\s+([^%]+)\s*%\}(.*?)\{%\s*endif\s*%\}/gs, (match, condition, content) => {
        const value = evaluateCondition(condition.trim(), data);
        return value ? renderTemplate(content, data) : '';
    });

    // Handle else conditionals: {% if condition %}content{% else %}alternative{% endif %}
    result = result.replace(/\{%\s*if\s+([^%]+)\s*%\}(.*?)\{%\s*else\s*%\}(.*?)\{%\s*endif\s*%\}/gs, (match, condition, ifContent, elseContent) => {
        const value = evaluateCondition(condition.trim(), data);
        return value ? renderTemplate(ifContent, data) : renderTemplate(elseContent, data);
    });

    // Handle variable replacement - use a custom function to properly handle nested braces
    result = replaceVariables(result, data);


    return result;
}

/**
 * Replace variables with proper handling of nested braces
 * @param {string} text - Text to process
 * @param {object} data - Data context
 * @returns {string} - Processed text
 */
function replaceVariables(text, data) {
    let result = '';
    let i = 0;

    while (i < text.length) {
        // Look for opening {{
        if (text[i] === '{' && text[i + 1] === '{') {
            // Find the closing }}
            let j = i + 2;
            let braceCount = 1;
            let content = '';

            while (j < text.length && braceCount > 0) {
                if (text[j] === '{' && text[j + 1] === '{') {
                    braceCount++;
                    content += text[j] + text[j + 1];
                    j += 2;
                } else if (text[j] === '}' && text[j + 1] === '}') {
                    braceCount--;
                    if (braceCount > 0) {
                        content += text[j] + text[j + 1];
                    }
                    j += 2;
                } else {
                    content += text[j];
                    j++;
                }
            }

            if (braceCount === 0) {
                // We found a complete variable
                const variableContent = content.trim();


                // Process the variable
                let value = processVariable(variableContent, data);
                result += value;
                i = j;
            } else {
                // Malformed variable, just add the character
                result += text[i];
                i++;
            }
        } else {
            result += text[i];
            i++;
        }
    }

    return result;
}

/**
 * Process a single variable with filters
 * @param {string} variableContent - The content inside {{ }}
 * @param {object} data - Data context
 * @returns {string} - Processed value
 */
function processVariable(variableContent, data) {
    const cleanKey = variableContent.trim();

    // Split filters first, but keep them for later
    const parts = cleanKey.split('|');
    const expression = parts[0].trim();
    const filters = parts.slice(1).map(f => f.trim());

    // Handle ternary operations
    let value;
    if (expression.includes('?') && expression.includes(':')) {
        const [condition, rest] = expression.split('?').map(s => s.trim());
        const [trueValue, falseValue] = rest.split(':').map(s => s.trim());

        // Evaluate the condition
        const conditionResult = evaluateExpression(data, condition);

        // Get the appropriate value based on condition
        value = conditionResult ?
            trueValue.replace(/^['"]|['"]$/g, '') :
            falseValue.replace(/^['"]|['"]$/g, '');
    } else {
        value = evaluateExpression(data, expression);
    }

    // Apply filters in sequence if there are any
    if (filters.length > 0) {
        return filters.reduce((val, filter) => applyFilter(val, filter, data), value);
    }

    return value !== undefined && value !== null ? value : '';

}

/**
 * Legacy function name for compatibility
 */
export function loadTemplate(template, data) {
    return renderTemplate(template, data);
}

/**
 * Evaluate expressions including array literals and object access
 * @param {object} data - Data context
 * @param {string} expression - Expression to evaluate
 * @returns {*} - Evaluated value
 */
/**
 * Evaluate expressions including array literals and object access
 * @param {object} data - Data context
 * @param {string} expression - Expression to evaluate
 * @returns {*} - Evaluated value
 */
function evaluateExpression(data, expression) {
    expression = expression.trim();

    // Handle array literals like [results.k]
    if (expression.startsWith('[') && expression.endsWith(']')) {
        const innerExpression = expression.slice(1, -1).trim();
        const value = getNestedValue(data, innerExpression);

        // Create a new array safely without modifying the original
        if (Array.isArray(value)) {
            return [...value]; // Use spread operator instead of Array.from()
        }

        // If it's a single value, create a new array
        if (value !== undefined && value !== null) {
            return [value]; // Direct array literal instead of Array.from()
        }

        return [];
    }

    // Handle regular expressions
    const result = getNestedValue(data, expression);
    return result;
}


/**
 * Get nested object value by dot notation and bracket notation
 * @param {object} obj - Object to search in
 * @param {string} path - Path like 'data.title', 'items.0.name', 'icons[key]', 'data.items[0].name'
 * @returns {*} - Value at the path or undefined
 */
function getNestedValue(obj, path) {
    // Handle bracket notation like icons[key] or data.items[0] (but not array literals)
    if (path.includes('[') && path.includes(']') && !path.startsWith('[')) {
        return evaluateBracketPath(obj, path);
    }

    // Handle simple dot notation with safer object access
    const result = path.split('.').reduce((current, key) => {
        if (current && typeof current === 'object' && current[key] !== undefined) {
            return current[key];
        }
        // Handle array indices
        if (current && Array.isArray(current) && !isNaN(parseInt(key))) {
            const index = parseInt(key);
            return index < current.length ? current[index] : undefined;
        }
        return undefined;
    }, obj);

    return result;
}


/**
 * Evaluate paths with bracket notation like icons[key] or data.items[index]
 * @param {object} obj - Object to search in
 * @param {string} path - Path with bracket notation
 * @returns {*} - Value at the path or undefined
 */
function evaluateBracketPath(obj, path) {
    let current = obj;
    let currentPath = '';
    let i = 0;

    while (i < path.length) {
        if (path[i] === '[') {
            // Process the path before the bracket
            if (currentPath) {
                current = getNestedValue(current, currentPath);
                currentPath = '';
            }

            // Find the closing bracket
            let j = i + 1;
            let bracketContent = '';
            while (j < path.length && path[j] !== ']') {
                bracketContent += path[j];
                j++;
            }

            if (j < path.length && path[j] === ']') {
                // Evaluate the bracket content
                let key;
                if (bracketContent.startsWith('"') && bracketContent.endsWith('"')) {
                    // String literal: icons["male"]
                    key = bracketContent.slice(1, -1);
                } else if (bracketContent.startsWith("'") && bracketContent.endsWith("'")) {
                    // String literal: icons['male']
                    key = bracketContent.slice(1, -1);
                } else if (!isNaN(parseInt(bracketContent))) {
                    // Numeric index: items[0]
                    key = parseInt(bracketContent);
                } else {
                    // Variable reference: icons[key]
                    key = getNestedValue(obj, bracketContent);
                }

                // Access the property
                if (current && current[key] !== undefined) {
                    current = current[key];
                } else {
                    return undefined;
                }

                i = j + 1;
                // Skip dot after closing bracket
                if (i < path.length && path[i] === '.') {
                    i++;
                }
            } else {
                return undefined; // Malformed bracket
            }
        } else if (path[i] === '.') {
            // Process accumulated path
            if (currentPath) {
                current = getNestedValue(current, currentPath);
                currentPath = '';
            }
            i++;
        } else {
            currentPath += path[i];
            i++;
        }
    }

    // Process any remaining path
    if (currentPath) {
        current = getNestedValue(current, currentPath);
    }

    return current;
}

/**
 * Evaluate simple conditions
 * @param {string} condition - Condition string
 * @param {object} data - Data context
 * @returns {boolean} - Condition result
 */
function evaluateCondition(condition, data) {
    // Handle simple existence checks
    if (!condition.includes(' ')) {
        const value = getNestedValue(data, condition);
        return !!value;
    }

    // Handle equality checks: variable == 'value'
    if (condition.includes('==')) {
        const [left, right] = condition.split('==').map(s => s.trim());
        const leftValue = getNestedValue(data, left);
        const rightValue = right.replace(/['"]/g, ''); // Remove quotes
        return leftValue == rightValue;
    }

    // Handle inequality checks: variable != 'value'
    if (condition.includes('!=')) {
        const [left, right] = condition.split('!=').map(s => s.trim());
        const leftValue = getNestedValue(data, left);
        const rightValue = right.replace(/['"]/g, ''); // Remove quotes
        return leftValue != rightValue;
    }

    // Handle negation: not variable
    if (condition.startsWith('not ')) {
        const varName = condition.substring(4).trim();
        const value = getNestedValue(data, varName);
        return !value;
    }

    // Default: check if variable exists and is truthy
    const value = getNestedValue(data, condition);
    return !!value;
}

/**
 * Apply simple filters to values
 * @param {*} value - Value to filter
 * @param {string} filter - Filter name with optional parameters
 * @param {object} data - Data context for variable evaluation
 * @returns {*} - Filtered value
 */
function applyFilter(value, filter, data) {


    if (value === undefined || value === null) {

        return '';
    }

    // Handle filters with parameters like replace({'.':','})
    if (filter.includes('(')) {
        const [filterName, params] = filter.split('(');
        const paramString = params.replace(')', '');



        switch (filterName.trim()) {
            case 'replace':
                return applyReplaceFilter(value, paramString, data);
            default:
                console.warn(`Unknown filter with parameters: ${filterName}`);
                return value;
        }
    }

    // Handle simple filters

    switch (filter.toLowerCase()) {
        case 'upper':
            return String(value).toUpperCase();
        case 'lower':
            return String(value).toLowerCase();
        case 'capitalize':
            return String(value).charAt(0).toUpperCase() + String(value).slice(1);
        case 'join':

            const result = Array.isArray(value) ? value.join('') : String(value);

            return result;
        case 'length':
            return Array.isArray(value) ? value.length : String(value).length;
        default:
            console.warn(`Unknown filter: ${filter}`);
            return value;
    }
}

/**
 * Apply replace filter with object parameter
 * @param {*} value - Value to process
 * @param {string} paramString - Parameter string like {'.':','}
 * @param {object} data - Data context for variable evaluation
 * @returns {string} - Processed value
 */
function applyReplaceFilter(value, paramString, data) {


    try {
        // Parse the parameter object (simple JSON-like parsing)
        const replacements = parseReplaceParams(paramString, data);


        let result = String(value);


        // Apply all replacements
        for (const [search, replaceItem] of Object.entries(replacements)) {
            const replaceWith:string = String(replaceItem || '');
            result = result.replace(new RegExp(escapeRegExp(search), 'g'),  replaceWith);
        }

        return result;
    } catch (error) {
        console.warn('Error applying replace filter:', error);
        return value;
    }
}

/**
 * Parse replace parameters like {'.':',', '$$age$$': results.k}
 * @param {string} paramString - Parameter string
 * @param {object} data - Data context for variable evaluation
 * @returns {object} - Parsed replacements object
 */
function parseReplaceParams(paramString, data) {


    // Remove outer braces and whitespace
    const cleaned = paramString.trim().replace(/^\{|\}$/g, '');


    if (!cleaned) return {};

    const replacements = {};

    // Split by commas, but be careful with quoted strings
    const parts = splitReplaceParams(cleaned);


    for (const part of parts) {
        const colonIndex = part.indexOf(':');
        if (colonIndex === -1) continue;

        const key = part.substring(0, colonIndex).trim();
        const value = part.substring(colonIndex + 1).trim();

        // Remove quotes from key
        const cleanKey = key.replace(/^['"]|['"]$/g, '');

        // Handle value - it might be a variable reference or a quoted string
        let cleanValue;
        if (value.startsWith('"') && value.endsWith('"')) {
            // Quoted string literal
            cleanValue = value.slice(1, -1);
        } else if (value.startsWith("'") && value.endsWith("'")) {
            // Quoted string literal
            cleanValue = value.slice(1, -1);
        } else {
            // Variable reference - evaluate it

            const evaluatedValue = getNestedValue(data, value);

            cleanValue = evaluatedValue !== undefined && evaluatedValue !== null ? String(evaluatedValue) : value;
        }


        replacements[cleanKey] = cleanValue;
    }

    return replacements;
}

/**
 * Split replace parameters by comma, respecting quoted strings
 * @param {string} str - String to split
 * @returns {string[]} - Array of parameter parts
 */
function splitReplaceParams(str) {
    const parts: string[] = [];
    let current = '';
    let inQuotes = false;
    let quoteChar = '';

    for (let i = 0; i < str.length; i++) {
        const char = str[i];

        if (!inQuotes && (char === '"' || char === "'")) {
            inQuotes = true;
            quoteChar = char;
            current += char;
        } else if (inQuotes && char === quoteChar) {
            inQuotes = false;
            quoteChar = '';
            current += char;
        } else if (!inQuotes && char === ',') {
            parts.push(current.trim());
            current = '';
        } else {
            current += char;
        }
    }

    if (current.trim()) {
        parts.push(current.trim());
    }

    return parts;
}

/**
 * Escape special regex characters
 * @param {string} string - String to escape
 * @returns {string} - Escaped string
 */
function escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
